// Per-gate operational detail. For every gate that has a plane (parked,
// arriving, or pushing) we manufacture believable-looking state:
//   - the assigned drivers (FWD / AFT) with status (accepted / pending /
//     late / in_run / arrived)
//   - bag bins A–F with total + scanned counts (live "download" progress)
//   - outbound connections from this flight, with connection time +
//     hot/normal/cold flag + countdown timer to push
//   - the next inbound scheduled at this gate (+ whether it's on the
//     ground / taxiing / not yet scheduled)
//   - any open issues (driver hasn't accepted, driver late, missing tug)
//
// The dispatcher's gate-detail panel reads this to surface everything
// they'd otherwise have to chase across radios, the TDR, and a sheet.
//
// Static seed for now — easy to wire to real data once the backend
// catches up.

import { DRIVERS, GATE_STATE, GATE_BY_ID } from './mockLiveState';
import { nextInboundAtGate } from '../mockBank';

// Demo wall-clock anchor — every "next inbound" lookup uses this as
// "now" so the snapshot is consistent across the map.
const DEMO_NOW_MIN = 12 * 60;

// Deterministic RNG so the demo is reproducible.
function makeRng(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const rng = makeRng(8675309);
function pick(arr) { return arr[Math.floor(rng() * arr.length)]; }
function rand(lo, hi) { return Math.floor(rng() * (hi - lo + 1)) + lo; }

const DRIVER_STATUSES = [
  { code: 'accepted', label: 'Accepted',  weight: 60 },
  { code: 'pending',  label: 'Pending',   weight: 12 },
  { code: 'arrived',  label: 'At Gate',   weight: 18 },
  { code: 'in_run',   label: 'In Run',    weight: 6  },
  { code: 'late',     label: 'Late',      weight: 4  },
];
function pickDriverStatus() {
  const total = DRIVER_STATUSES.reduce((s, x) => s + x.weight, 0);
  let r = rng() * total;
  for (const s of DRIVER_STATUSES) {
    r -= s.weight;
    if (r <= 0) return s.code;
  }
  return 'accepted';
}

// Pick two distinct drivers (one FWD, one AFT) for a flight.
let driverCursor = 0;
function nextTwoDrivers() {
  const a = DRIVERS[driverCursor++ % DRIVERS.length];
  const b = DRIVERS[driverCursor++ % DRIVERS.length];
  return [a, b];
}

// Build connection stops for an outbound from this inbound flight.
const DESTS = ['EUG','BOI','ABQ','PDX','ELP','SEA','SLC','COS','LGB','BUR','RNO','TUS','SMF','ICT','BIL','GEG','OKC','TUL'];
function buildConnections(rng) {
  const count = rand(3, 6);
  const out = [];
  for (let i = 0; i < count; i++) {
    const dest = pick(DESTS);
    const connMin = rand(20, 180);   // minutes between this inbound landing and outbound push
    const bags    = rand(1, 14);
    const flight  = `WN ${rand(100, 4900)}`;
    const gate    = `C${rand(23, 67)}`;
    const isHot  = connMin <= 30;
    const isCold = connMin >  150;
    // Whether the bag has actually made it onto the outbound. Hot bags
    // are still in transit most of the time (that's the whole point of
    // the rush); cold bags are usually pulled but ~30% are still
    // pending — that's the case the dispatcher needs to know about.
    const bagDelivered = isHot ? rng() > 0.55
                        : isCold ? rng() > 0.30
                        : rng() > 0.15;
    out.push({
      flightNumber: flight,
      destination: dest,
      gate,
      connectionMin: connMin,
      bags,
      isHot,
      isCold,
      bagDelivered,
      // confidence the bag will make the connection (0–100)
      makeConfidence: connMin <= 25 ? rand(35, 70)
                    : connMin <= 35 ? rand(70, 90)
                    : rand(92, 99),
    });
  }
  return out;
}

// Build bin scan progress (A–F). Total bags per bin between 4 and 28;
// scanned is some fraction depending on overall gate progress.
function buildBins(progress) {
  const bins = ['A','B','C','D','E','F'];
  return bins.map(letter => {
    const total = rand(4, 28);
    // Per-bin progress jitters around the gate-level progress
    const localPct = Math.max(0, Math.min(1, progress + (rng() - 0.5) * 0.3));
    const scanned = Math.round(total * localPct);
    return { letter, total, scanned };
  });
}

// Look up the next real inbound scheduled at this gate after demo "now".
// Returns null only if there are truly no more flights today at this
// gate. Phase derives from minutes-away so the dispatcher can tell at a
// glance whether the next aircraft is taxiing, on the ground, or still
// in the air.
export function nextInboundFor(gateId) {
  const next = nextInboundAtGate(gateId, DEMO_NOW_MIN);
  if (!next) return null;
  const minutesAway = next.etaMins - DEMO_NOW_MIN;
  let phase;
  if (minutesAway < 12)      phase = 'taxiing';
  else if (minutesAway < 25) phase = 'on_ground';
  else                       phase = 'in_air';
  return {
    flightNumber: next.flightNumber,
    origin:       next.from,
    eta:          next.eta,
    minutesAway,
    phase,
  };
}

// Detect issues a dispatcher should ACT on. Severity drives how the
// board / map highlight the gate:
//
//   red    = act now. Operationally blocked or about to miss.
//   gold   = heads-up. Will need attention soon if untouched.
//   blue   = informational. Cold bags pending, etc.
//
// Quiet gates (no issues) get no color treatment — that's the point.
function deriveIssues(detail) {
  const issues = [];

  // RED — missing assigned driver. Can't operate without crew.
  if (detail.drivers.length < 2) {
    issues.push({ kind: 'missing_driver', severity: 'red',
      label: `${2 - detail.drivers.length} driver unassigned` });
  }
  // RED — driver flagged late
  detail.drivers.forEach(d => {
    if (d.status === 'late') {
      issues.push({ kind: 'driver_late', severity: 'red',
        label: `${d.name} late`, driverId: d.id });
    } else if (d.status === 'pending') {
      // GOLD — driver hasn't accepted yet; nag-able but not a blocker
      issues.push({ kind: 'driver_pending', severity: 'gold',
        label: `${d.name} hasn't accepted`, driverId: d.id });
    }
  });
  // GOLD — no tug assigned
  if (!detail.tug) {
    issues.push({ kind: 'tug_unassigned', severity: 'gold',
      label: 'No tug assigned' });
  }
  // Hot bag triage. Two tiers:
  //   - critical: <15 min until the connection's outbound pushes and the
  //     bag isn't on the outbound yet. Truly act now.
  //   - at risk:  confidence below 75%. Heads-up, monitor.
  detail.connections.forEach(c => {
    const bagOnboard = c.bagDelivered === true;
    if (c.isHot && c.connectionMin <= 15 && !bagOnboard) {
      issues.push({
        kind: 'hot_critical', severity: 'red',
        label: `Hot bag → ${c.flightNumber} pushes in ${c.connectionMin}m`,
        flightNumber: c.flightNumber,
      });
    } else if (c.isHot && c.makeConfidence < 75) {
      issues.push({
        kind: 'hot_at_risk', severity: 'gold',
        label: `Hot bag → ${c.flightNumber} (${c.makeConfidence}% make)`,
        flightNumber: c.flightNumber,
      });
    }
  });
  // GOLD — download taking too long. Phase is 'parked' (downloading)
  // but bags scanning has stalled below where we'd expect this far in.
  if (detail.state === 'parked' && detail.bagPct < 80
      && detail.phaseStartedSecAgo > 8 * 60) {
    issues.push({
      kind: 'download_delayed', severity: 'gold',
      label: `Download stalled at ${detail.bagPct}%`,
    });
  }
  // BLUE — informational. Cold connections still pending; nothing to do
  // urgently but the dispatcher should know there's outstanding work.
  const hasColdPending = detail.connections.some(c =>
    c.isCold && c.bagDelivered !== true);
  if (hasColdPending && detail.bagPct < 100) {
    issues.push({
      kind: 'cold_pending', severity: 'blue',
      label: 'Cold bags still pending',
    });
  }
  return issues;
}

// Derive the operational phase of a turn from the raw state + bag
// progress. Used by the map's gate cards to pick what to show and
// what color the border is. Phases:
//   inbound  — plane is taxiing in
//   arriving — at gate, prepping (pre-download)
//   download — bags coming off
//   upload   — bags going on for the outbound
//   push     — pushing back from gate
//   empty    — no flight at gate
function derivePhase(state, bagPct) {
  if (state === 'empty')    return 'empty';
  if (state === 'arriving') return 'inbound';
  if (state === 'pushing')  return 'push';
  if (state === 'parked') {
    // Once download is done the next thing is loading the outbound —
    // there's no "service" gap in the dispatcher's mental model.
    return bagPct < 100 ? 'download' : 'upload';
  }
  return 'unknown';
}

// Build everything for one gate (only for gates with a plane).
function buildGateDetail(gate, gateState) {
  const [d1, d2] = nextTwoDrivers();
  const drivers = [d1, d2].filter(Boolean).map((d, i) => ({
    id: d.id,
    name: d.name,
    badge: d.badge,
    role: i === 0 ? 'FWD' : 'AFT',
    status: pickDriverStatus(),
  }));
  // ~10% of gates: only ONE driver assigned (issue)
  if (rng() < 0.1) drivers.pop();

  const progress = gateState.state === 'parked'   ? rng() * 0.5 + 0.4   // mid-download
                : gateState.state === 'arriving'  ? 0
                : gateState.state === 'pushing'   ? 1
                : 0;
  const bins = buildBins(progress);
  const totalBags   = bins.reduce((s, b) => s + b.total,   0);
  const scannedBags = bins.reduce((s, b) => s + b.scanned, 0);

  const connections = buildConnections(rng);
  const tugAssigned = rng() > 0.08;

  const startBagPct = totalBags > 0 ? Math.round((scannedBags / totalBags) * 100) : 0;

  const detail = {
    gateId: gate.id,
    gateNum: gate.num,
    flightNumber: gateState.flightNumber,
    origin: gateState.origin,
    destination: pick(['EUG','BOI','ABQ','PDX','ELP','SEA','SLC','COS','LGB']),
    tail: gateState.tail,
    eta: gateState.eta,
    etd: fmtTime(parseEta(gateState.eta) + 45 + rand(-10, 25)),
    state: gateState.state,
    drivers,
    bins,
    totalBags,
    scannedBags,
    bagPct: startBagPct,
    connections,
    tug: tugAssigned ? `T-${rand(1000, 9999)}` : null,
    nextInbound: nextInboundFor(gate.id),
    phase: derivePhase(gateState.state, startBagPct),
    // Demo-only "phase started" offset (in seconds before now).
    // The card uses this to drive a live "DOWNLOAD 02:14" timer.
    phaseStartedSecAgo: rand(30, 480),
    // Upload progress (only meaningful when phase === 'upload')
    uploadTotal:   rand(60, 130),
    uploadLoaded:  rand(0, 60),
    hotPending:    rand(0, 2),
  };
  detail.issues = deriveIssues(detail);
  return detail;
}

function parseEta(eta) {
  if (!eta) return 7 * 60;
  const m = String(eta).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return 7 * 60;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}
function fmtTime(mins) {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// Build details for every gate that has a plane.
export const GATE_DETAILS = (() => {
  const map = new Map();
  GATE_STATE.forEach((state, gateId) => {
    if (state.state === 'empty') return;
    const gate = GATE_BY_ID.get(gateId);
    if (!gate) return;
    map.set(gateId, buildGateDetail(gate, state));
  });
  return map;
})();

// Aggregate issue count per gate (for badge rendering on the map).
export function issuesForGate(gateId) {
  const d = GATE_DETAILS.get(gateId);
  if (!d || d.issues.length === 0) return { count: 0, severity: null };
  return { count: d.issues.length, severity: alertLevelFor(d) };
}

// Worst severity present in the detail's issues, or null if quiet.
// red > gold > blue. Drives every "is this gate worth showing color
// for" decision in the board / map.
export function alertLevelFor(detail) {
  if (!detail || !detail.issues || detail.issues.length === 0) return null;
  if (detail.issues.some(i => i.severity === 'red'))  return 'red';
  if (detail.issues.some(i => i.severity === 'gold')) return 'gold';
  if (detail.issues.some(i => i.severity === 'blue')) return 'blue';
  return null;
}

// Single source of truth for alert colors. Gold matches the canonical
// Priority hex so the dispatcher reads gold the same way everywhere.
export const ALERT_COLORS = {
  red:  '#FF0000',
  gold: '#F9B612',
  blue: '#0277BD',
};

// Phase labels for next inbound. Gold reserved for Priority semantics
// only — taxiing-in is a pre-active state, so it gets the cold-blue
// tier with the rest of the inbound stack.
export const PHASE_LABELS = {
  taxiing:   { label: 'Taxiing in', color: '#0277BD' },
  on_ground: { label: 'On ground',  color: '#00BCD4' },
  in_air:    { label: 'In air',     color: '#94A3B8' },
};

// Driver-status display meta. Pending = needs attention but isn't a
// connection priority; cyan signals "active/awaiting" without claiming
// the priority gold.
export const DRIVER_STATUS_META = {
  accepted: { label: 'Accepted', color: '#4CAF50' },
  pending:  { label: 'Pending',  color: '#00BCD4' },
  arrived:  { label: 'At Gate',  color: '#00BCD4' },
  in_run:   { label: 'In Run',   color: '#2196F3' },
  late:     { label: 'Late',     color: '#FF0000' },
};
